import { useState } from 'react';
import { useStore, DEFAULT_MODEL, DEFAULT_COMPILE_URL } from '../state/store';
import { testApiKey } from '../lib/gemini';
import { IconClose, IconCheck } from './icons';

const FQBN_OPTIONS = [
  { value: 'esp32:esp32:esp32', label: 'ESP32 Dev Module (classic)' },
  { value: 'esp32:esp32:esp32s3', label: 'ESP32-S3' },
  { value: 'esp32:esp32:esp32s2', label: 'ESP32-S2' },
  { value: 'esp32:esp32:esp32c3', label: 'ESP32-C3' },
];

const FLASH_BAUDS = [115200, 460800, 921600];

export function SettingsModal() {
  const open = useStore((s) => s.settingsOpen);
  const setOpen = useStore((s) => s.setSettingsOpen);
  const settings = useStore((s) => s.settings);
  const setSetting = useStore((s) => s.setSetting);

  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  if (!open) return null;

  async function onTestKey(): Promise<void> {
    if (!settings.apiKey.trim()) {
      setTestResult({ ok: false, message: 'Enter an API key first.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    const result = await testApiKey(settings.apiKey.trim());
    setTestResult(result);
    setTesting(false);
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <div className="modal">
        <div className="modal-head">
          <div className="modal-title">Settings</div>
          <button className="modal-x" aria-label="Close settings" onClick={() => setOpen(false)}>
            <IconClose />
          </button>
        </div>

        <div className="modal-body">
          <div className="field">
            <div className="field-label">
              <span>Google AI Studio API key</span>
              <span className="field-hint">stored in this browser only</span>
            </div>
            <div className="field-row">
              <input
                type={showKey ? 'text' : 'password'}
                value={settings.apiKey}
                placeholder="AIza…"
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => {
                  setSetting('apiKey', e.target.value);
                  setTestResult(null);
                }}
              />
              <button className="btn" onClick={() => setShowKey((v) => !v)}>
                {showKey ? 'Hide' : 'Show'}
              </button>
              <button className="btn" onClick={() => void onTestKey()} disabled={testing}>
                {testing ? 'Testing…' : 'Test key'}
              </button>
            </div>
            <p className="field-note">
              Create a free key at aistudio.google.com. It is sent only from this browser to
              Google's API — never to any other server.
            </p>
            {testResult && <div className={`test-result ${testResult.ok ? 'ok' : 'err'}`}>{testResult.message}</div>}
          </div>

          <div className="field-grid">
            <div className="field">
              <div className="field-label">
                <span>Model</span>
              </div>
              <input
                type="text"
                value={settings.model}
                placeholder={DEFAULT_MODEL}
                spellCheck={false}
                onChange={(e) => setSetting('model', e.target.value.trim() || DEFAULT_MODEL)}
              />
              <p className="field-note">Default: {DEFAULT_MODEL}</p>
            </div>

            <div className="field">
              <div className="field-label">
                <span>Board target</span>
              </div>
              <select value={settings.fqbn} onChange={(e) => setSetting('fqbn', e.target.value)}>
                {FQBN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <div className="field-label">
                <span>Flash baud</span>
                <span className="field-hint">lower it if flashing fails</span>
              </div>
              <select value={settings.flashBaud} onChange={(e) => setSetting('flashBaud', Number(e.target.value))}>
                {FLASH_BAUDS.map((b) => (
                  <option key={b} value={b}>
                    {b} baud
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <div className="field-label">
                <span>Compile service</span>
              </div>
              <input
                type="text"
                value={settings.compileUrl}
                placeholder={DEFAULT_COMPILE_URL}
                spellCheck={false}
                onChange={(e) => setSetting('compileUrl', e.target.value.trim())}
              />
              <p className="field-note">Local arduino-cli bridge (npm run dev:service).</p>
            </div>
          </div>

          <div className="field" style={{ marginBottom: 0 }}>
            <div className="field-label">
              <span>Serial monitor baud</span>
            </div>
            <select value={settings.serialBaud} onChange={(e) => setSetting('serialBaud', Number(e.target.value))}>
              {[9600, 19200, 38400, 57600, 115200, 230400].map((b) => (
                <option key={b} value={b}>
                  {b} baud
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={() => setOpen(false)}>
            <IconCheck />
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
